import { useEffect, useState } from "react";
import { api } from "../api";
import { invalidatePortalConfig, useDisplayTimezone, usePortalConfig } from "../portalConfig";
import { Btn, Card, Field, Input, PasswordInput, Select, SwitchRow, formatWhen } from "../components/Shared";
import { groupTimezones } from "../components/Portal";
import { useDocumentWord } from "../documentWord";
import StorageCard from "./StorageCard";

// ── Settings tab ─────────────────────────────────────────────────────────────

export default function SettingsTab() {
  const word = useDocumentWord();
  const [settings, setSettings] = useState(null);
  const [values, setValues]     = useState({});
  const [saving, setSaving]     = useState({});
  const [saved, setSaved]       = useState({});
  const [error, setError]       = useState(null);
  const [fiscal, setFiscal]     = useState(null);
  const [fyYear, setFyYear]     = useState("");
  const [fyDate, setFyDate]     = useState("");
  const [fySaving, setFySaving] = useState(false);
  const { ppms_enabled: ppmsEnabled } = usePortalConfig();
  // The facility's one PUMAPI key (2026-09-28). Only whether it is set is ever
  // held here: `GET /api/admin/ppms-settings` answers `ppms_api_key_set` and
  // never the key, and this page copies that one boolean out rather than the
  // whole response, so a server that did send the key would still not show it.
  const [ppmsKeySet, setPpmsKeySet] = useState(null);
  const [ppmsKey, setPpmsKey]       = useState("");
  const [ppmsSaving, setPpmsSaving] = useState(false);
  const [ppmsSaved, setPpmsSaved]   = useState(false);
  // The PPMS catalogue (2026-10-20): what the key reads — PPMS's whole project
  // list — and when it last did. Its own result and error, on the card, so a
  // refused refresh is not mistaken for a failed setting above.
  const zone = useDisplayTimezone();
  const [catalog, setCatalog]               = useState(null);
  const [refreshing, setRefreshing]         = useState(false);
  const [refreshResult, setRefreshResult]   = useState(null);
  const [refreshError, setRefreshError]     = useState(null);

  useEffect(() => {
    api.get("/api/admin/settings")
      .then(data => {
        setSettings(data);
        setValues(Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v.value])));
      })
      .catch(e => setError(e.message));
    api.get("/api/admin/fiscal-years")
      .then(setFiscal)
      .catch(e => setError(e.message));
  }, []);

  // Only when the portal talks to PPMS at all: without the explorer there is
  // nothing to verify a key against and nothing that would use it.
  useEffect(() => {
    if (!ppmsEnabled) return;
    api.get("/api/admin/ppms-settings")
      .then(data => setPpmsKeySet(!!data?.ppms_api_key_set))
      .catch(e => setError(e.message));
    api.get("/api/ppms/catalog/status")
      .then(setCatalog)
      .catch(e => setRefreshError(e.message));
  }, [ppmsEnabled]);

  // The status is read again whichever way the refresh ends: a failed one
  // records its error there, and the card should show what the server now holds.
  // The outcome is held until then and set once, with the new status, so the
  // card never shows "refreshed" beside the old count.
  const refreshCatalog = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    setRefreshError(null);
    let result = null, failure = null;
    try {
      const out = await api.post("/api/admin/ppms-catalog/refresh");
      result = out?.detail || "Catalogue refreshed.";
    } catch (e) {
      failure = e.message;
    }
    try {
      setCatalog(await api.get("/api/ppms/catalog/status"));
    } catch (e) {
      failure = failure || e.message;
    } finally {
      setRefreshResult(result);
      setRefreshError(failure);
      setRefreshing(false);
    }
  };

  // Its own write-only route, not `/api/admin/settings/<key>`: that registry
  // hands every value back in its GET and audits old → new verbatim. The
  // server proves the key against PPMS before storing it and refuses it in
  // PPMS's own words otherwise — shown as they come, with the key left in the
  // box to correct.
  const savePpmsKey = async () => {
    setPpmsSaving(true);
    setPpmsSaved(false);
    setError(null);
    try {
      const out = await api.patch("/api/admin/ppms-settings", { ppms_api_key: ppmsKey.trim() });
    setPpmsKeySet(!!out?.ppms_api_key_set);
      // Write-only: left in the box, it would be a credential on screen that
      // nothing needs any more.
      setPpmsKey("");
      setPpmsSaved(true);
      setTimeout(() => setPpmsSaved(false), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setPpmsSaving(false);
    }
  };

  const closeYear = async () => {
    setFySaving(true);
    setError(null);
    try {
      await api.put(`/api/admin/fiscal-years/${fyYear}`, { closing_date: fyDate });
      setFiscal(await api.get("/api/admin/fiscal-years"));
      setFyYear(""); setFyDate("");
    } catch (e) {
      setError(e.message);
    } finally {
      setFySaving(false);
    }
  };

  // `value` is passed by a switch, which saves the moment it is flipped:
  // the new value is not in `values` yet when the save starts.
  const handleSave = async (key, value = values[key]) => {
    setSaving(s => ({ ...s, [key]: true }));
    setSaved(s => ({ ...s, [key]: false }));
    setError(null);
    try {
      await api.patch(`/api/admin/settings/${key}`, { value });
      setValues(v => ({ ...v, [key]: value }));
      // These settings are read from /auth/config by every view that shows a
      // time or a session limit, and that response is cached for the lifetime
      // of the page. Without this the save succeeds and the screen keeps
      // showing the old zone until a full reload.
      invalidatePortalConfig();
      setSaved(s => ({ ...s, [key]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [key]: false })), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  if (!settings) return <div style={{ textAlign: "center", padding: "48px 0", color: "#6b7280" }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 520 }}>
      {error && (
        <div role="alert" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}
      <Card>
        <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700 }}>Portal Settings</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Driven by the type the backend declares, not by what the one
              original setting happened to be: this used to render a number
              input unconditionally and print the word "hours" after every
              value, so a second setting of any other shape was impossible. */}
          {Object.entries(settings).map(([key, meta]) => meta.type === "bool" ? (
            /* A switch saves when it is flipped, and shows the stored value
               until the server has accepted the new one: a refused switch
               (manual project creation while PPMS is not set up) stays where it
               was, with the server's reason in the alert above. */
            <div key={key}>
              <SwitchRow
                label={meta.label}
                checked={(values[key] ?? meta.default) === "true"}
                disabled={!!saving[key]}
                onChange={on => handleSave(key, on ? "true" : "false")}
                hint={meta.help + (saved[key] ? " ✓ Saved" : "")}
              />
            </div>
          ) : (
            <div key={key}>
              <Field
                label={meta.label}
                /* A setting whose default is blank has "off" as a real value —
                   the two retention numbers do — so the hint has to say so, or
                   the range reads as a requirement and nobody finds the way
                   back to off. Decided from the declared default rather than a
                   list here, which is the same rule the backend validates by. */
                hint={meta.type === "int"
                  ? `Min: ${meta.min} · Max: ${meta.max}`
                    + (meta.default === "" ? " · blank means never" : "")
                  : undefined}
              >
                <div style={{ display: "flex", gap: 8 }}>
                  {meta.type === "select" ? (
                    /* Options go in the `options` prop — Select maps over it
                       internally and ignores children. Passing <option>
                       children here instead left options undefined, and the
                       resulting undefined.map() unmounted the whole app: the
                       blank Settings page reported on 2026-08-12. */
                    /* A select that names its own labels (`option_labels`,
                       2026-10-28: the customer-document kind) shows them and
                       saves the raw value; through `groupTimezones` it would
                       show "statement_of_work" under *Other*. */
                    <Select
                      options={meta.option_labels
                        ? (meta.options || []).map(o => ({ value: o, label: meta.option_labels[o] ?? o }))
                        : groupTimezones(meta.options)}
                      value={values[key] ?? ""}
                      onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                      style={{ maxWidth: 280 }}
                    />
                  ) : (
                    <Input
                      type="number"
                      min={meta.min}
                      max={meta.max}
                      value={values[key] ?? ""}
                      onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                      style={{ maxWidth: 120 }}
                    />
                  )}
                  <Btn
                    onClick={() => handleSave(key)}
                    disabled={saving[key]}
                    style={{ fontSize: 13 }}
                  >
                    {saving[key] ? "Saving…" : saved[key] ? "✓ Saved" : "Save"}
                  </Btn>
                </div>
              </Field>
              {meta.help && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>{meta.help}</div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* The portal's one storage (2026-11-03): it was each group's, under
          Facility settings, until a project moved to another group was found
          to split its data across two buckets. */}
      <div style={{ height: 20 }} />
      <StorageCard />

      {ppmsEnabled && ppmsKeySet !== null && (
        <>
          <div style={{ height: 20 }} />
          <Card>
            <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700 }}>PPMS</h3>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
              The facility's one PUMAPI key. It is used to read PPMS's whole project list
              into the catalogue that projects are found and imported from.
              The key can write at PPMS, so it is never shown again once saved.
            </p>
            <Field label="PUMAPI key"
                   hint={ppmsKeySet
                     ? "Set — leave blank to keep it, or paste a new one to replace it. It is checked against PPMS before it is stored."
                     : "Not set — the PPMS catalogue cannot be refreshed until it is. From PPMS: My PPMS → API. It is checked against PPMS before it is stored."}>
              <PasswordInput value={ppmsKey} onChange={e => { setPpmsKey(e.target.value); setPpmsSaved(false); }}
                             placeholder={ppmsKeySet ? "•••••••• (unchanged)" : ""}
                             autoComplete="new-password" />
            </Field>
            <div style={{ marginTop: 12 }}>
              {/* Disabled while blank: blank KEEPS the stored key on the server
                  too, and there is deliberately no way to clear it from here. */}
              <Btn onClick={savePpmsKey} disabled={ppmsSaving || !ppmsKey.trim()}
                   style={{ fontSize: 13 }}>
                {ppmsSaving ? "Saving…" : ppmsSaved ? "✓ Saved" : "Save key"}
              </Btn>
            </div>
            {/* The schedule's two switches live in Portal settings above; this
                only says what they are set to. */}
            <div style={{ marginTop: 20, fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Project catalogue</div>
              {catalog && (
                <div style={{ color: "#374151", lineHeight: 1.6 }}>
                  <div>
                    Last refreshed: {catalog.refreshed_at ? formatWhen(catalog.refreshed_at, { zone }) : "never"}
                    {catalog.count != null && <> — {catalog.count} projects</>}
                  </div>
                  <div>
                    {/* The hour AND the job's minute, both from the status: it
                        printed the hour with ":00" until 2026-10-30, while the
                        refresh ran at :35. No minute, no time — never a guess. */}
                    {!catalog.enabled ? "The daily refresh is off."
                      : catalog.minute != null
                        ? <>Refreshed daily at {String(catalog.hour).padStart(2, "0")}:{String(catalog.minute).padStart(2, "0")}.</>
                        : "Refreshed once a day."}
                  </div>
                  {catalog.error && (
                    <div style={{ color: "#b91c1c" }}>Last attempt failed: {catalog.error}</div>
                  )}
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <Btn onClick={refreshCatalog} disabled={refreshing} style={{ fontSize: 13 }}>
                  {refreshing ? "Refreshing…" : "Refresh now"}
                </Btn>
              </div>
              {refreshResult && (
                <div style={{ marginTop: 8, color: "#15803d" }}>{refreshResult}</div>
              )}
              {refreshError && (
                <div role="alert" style={{ marginTop: 8, color: "#b91c1c" }}>{refreshError}</div>
              )}
            </div>
          </Card>
        </>
      )}

      <div style={{ height: 20 }} />

      <Card>
        <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700 }}>Closing a year</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
          A year's figures are every {word()} that <strong>ended</strong> on or before the
          date you set here. Until a year is closed, Statistics shows it as provisional and
          its figures can still change. Setting the date is what closes it — a {word()}
          approved afterwards belongs to the next year.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Field label="Year">
            <Input
              type="number" min="2000" max="2100" value={fyYear}
              onChange={e => setFyYear(e.target.value)}
              style={{ maxWidth: 110 }}
            />
          </Field>
          <Field label="Closed on">
            <Input
              type="date" value={fyDate}
              onChange={e => setFyDate(e.target.value)}
              style={{ maxWidth: 170 }}
            />
          </Field>
          <Btn
            onClick={closeYear}
            disabled={fySaving || !fyYear || !fyDate}
            style={{ fontSize: 13, marginBottom: 2 }}
          >
            {fySaving ? "Saving…" : "Close year"}
          </Btn>
        </div>
        {/* An empty list and a list still loading are different states, and only
            one of them is worth a sentence — the other is a flicker. */}
        {fiscal && fiscal.length > 0 && (
          <table className="responsive-table" style={{ width: "100%", marginTop: 18, borderCollapse: "collapse",
                          fontSize: 13 }}>
            <thead>
              <tr>
                <th scope="col" style={{ textAlign: "left", padding: "6px 8px",
                                         borderBottom: "1px solid #e5e7eb" }}>Year</th>
                <th scope="col" style={{ textAlign: "left", padding: "6px 8px",
                                         borderBottom: "1px solid #e5e7eb" }}>Closed on</th>
                <th scope="col" style={{ textAlign: "left", padding: "6px 8px",
                                         borderBottom: "1px solid #e5e7eb" }}>Closed by</th>
              </tr>
            </thead>
            <tbody>
              {fiscal.map(r => (
                <tr key={r.year}>
                  <td data-label="Year" style={{ padding: "6px 8px" }}>{r.year}</td>
                  <td data-label="Closed on" style={{ padding: "6px 8px" }}>{r.closing_date}</td>
                  <td data-label="Closed by" style={{ padding: "6px 8px" }}>{r.closed_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {fiscal && fiscal.length === 0 && (
          <p style={{ margin: "18px 0 0", fontSize: 13, color: "#6b7280" }}>
            No year has been closed yet, so every year in Statistics reads as provisional.
          </p>
        )}
      </Card>
    </div>
  );
}

